import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewEncapsulation } from '@angular/core';
import { FwPopoverComponent } from '@flywheel-io/vision';
import cronstrue from 'cronstrue';

import { OperationPopoverFields } from './operation-popover.enums';
import { XferExport, XferImport } from '../../../shared/models/xfer.model';
import { isExternalStorage } from '../../../shared/projects.utils';

@Component({
  host: { 'test-id': 'operation-popover' },
  selector: 'operation-popover',
  styleUrls: ['./operation-popover.component.scss'],
  templateUrl: './operation-popover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class OperationPopoverComponent {
  @Input() title: string;
  @Input() maxWidth = '400px';
  @Input() config: OperationPopoverFields[];

  @Input()
  set operation(value: XferExport | XferImport) {
    this.data = this.mapData(value);
    const cron = this.data[OperationPopoverFields.JobSchedule];

    if (cron) {
      this.humanReadableCron = cronstrue.toString(this.data[OperationPopoverFields.JobSchedule]);
    }
  }

  constructor(private cdr: ChangeDetectorRef) { }
  protected readonly dateMask = 'yyyy/MM/dd hh:mm';
  protected readonly OperationPopoverFields = OperationPopoverFields;
  protected readonly dateFields = [OperationPopoverFields.ScheduleStart, OperationPopoverFields.ScheduleEnd];
  protected data: Partial<Record<OperationPopoverFields, string>>;
  protected humanReadableCron = '';

  private readonly popoverClass = 'fw-popover-panel';
  private readonly idFields = [OperationPopoverFields.OriginId, OperationPopoverFields.SourceId, OperationPopoverFields.DestinationId];
  private readonly fieldsWithTooltip = [OperationPopoverFields.ExportJobName, OperationPopoverFields.ImportJobName, OperationPopoverFields.SourceUrl];

  private mapData(operation: XferExport | XferImport): Partial<Record<OperationPopoverFields, string>> {
    const { schedule, storage, origin, label, _id } = operation;
    const externalStorage = isExternalStorage(storage._id);
    const url = externalStorage ? operation.storage_url : storage.url;
    const data: Partial<Record<OperationPopoverFields, string>> = {
      [OperationPopoverFields.ImportJobName]: label,
      [OperationPopoverFields.ExportJobName]: label,
      [OperationPopoverFields.JobId]: _id,
      [OperationPopoverFields.Source]: storage.label,
      [OperationPopoverFields.SourceUrl]: url,
      [OperationPopoverFields.Destination]: storage.label,
      [OperationPopoverFields.DestinationUrl]: url,
      [OperationPopoverFields.Type]: origin.type,
      [OperationPopoverFields.OriginId]: origin.id,
    };

    if (externalStorage) {
      data[OperationPopoverFields.SourceId] = storage._id;
      data[OperationPopoverFields.DestinationId] = storage._id;
    }

    if (schedule) {
      data[OperationPopoverFields.JobScheduleId] = schedule?._id;
      data[OperationPopoverFields.JobSchedule] = schedule?.cron;

      if (schedule.start) {
        data[OperationPopoverFields.ScheduleStart] = schedule.start;
      }

      if (schedule.end) {
        data[OperationPopoverFields.ScheduleEnd] = schedule.end;
      }
    }

    return data;
  }

  protected getFieldKey(field: OperationPopoverFields): string {
    const prefix = 'operationsPopover.';

    if (this.idFields.includes(field)) {
      return `${prefix}id`;
    }

    return `${prefix}${field}`;
  }

  protected isTooltip(field: OperationPopoverFields): boolean {
    return this.fieldsWithTooltip.includes(field);
  }

  protected onMouseHover(): void {
    setTimeout(() => this.cdr.detectChanges());
  }

  protected onMouseLeave(event: MouseEvent, popover: FwPopoverComponent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (!relatedTarget?.classList.contains(this.popoverClass)) {
      popover.handlePanelLeave();
    }
  }
}
